import { useMemo } from "react";
import {
  ActivityLineChartDataPoint,
  UseChartDataResult,
  buildActivityChartData,
  buildLegendValues,
} from "../../utils/chartDataUtils";
import type { Dimension } from "../../../../domain/models/metrics";
import { normalizeMetricDateRange } from "../../utils/metricDateRange";
import { useAllAggregateMetrics } from "./useAllAggregateMetrics";

export { ActivityLineChartDataKey } from "../../utils/chartDataUtils";
export type {
  ActivityLineChartDataPoint,
  UseChartDataResult,
} from "../../utils/chartDataUtils";

interface UseInternalChartDataParams {
  startDate: Date;
  endDate: Date;
  dimension: Dimension;
  contentMetadataIds?: string[];
  countries?: string[];
  minSessionDurationInMinutes?: number;
}

export const useInternalChartData = (
  params: UseInternalChartDataParams,
): UseChartDataResult => {
  const {
    startDate,
    endDate,
    dimension,
    contentMetadataIds,
    countries,
    minSessionDurationInMinutes,
  } = params;

  const normalizedParams = useMemo<UseInternalChartDataParams>(() => {
    const normalizedRange = normalizeMetricDateRange(startDate, endDate);

    return {
      ...normalizedRange,
      dimension,
      contentMetadataIds,
      countries,
      minSessionDurationInMinutes,
    };
  }, [
    startDate,
    endDate,
    dimension,
    contentMetadataIds,
    countries,
    minSessionDurationInMinutes,
  ]);

  const { data, isLoading, isFetching } =
    useAllAggregateMetrics(normalizedParams);

  const chartData = useMemo<ActivityLineChartDataPoint[]>(
    () =>
      buildActivityChartData(
        normalizedParams.startDate,
        normalizedParams.endDate,
        normalizedParams.dimension,
        data?.hoursInVr,
        data?.uniqueUsers,
        data?.userSessions,
      ),
    [
      data,
      normalizedParams.startDate,
      normalizedParams.endDate,
      normalizedParams.dimension,
    ],
  );

  const legendValues = useMemo(
    () =>
      buildLegendValues(data?.hoursInVr, data?.uniqueUsers, data?.userSessions),
    [data],
  );

  return {
    chartData,
    legendValues,
    isLoading,
    isFetching,
  };
};
